import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  resource,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { firstValueFrom, map } from 'rxjs';

import { UserCapacities } from '../schemas';
import { Jira, UserService } from '../service';
import { ProjectsTime } from '../service/jira';
import { AugmentedItem, TimeTableData } from './chargeable-hours-page';

const ACTIVE_IMPACT = 20;
const RESPONSIBILITY_IMPACT = 25;
const TEAM_IMPACT = 25;
const PO_IMPACT = 20;
const SALES_IMPACT = 10;

type UserMap = Record<
  string,
  {
    id: string;
    capacity: number;
    salesCapacity: number;
  }
>;

type Rec = [string, number, AugmentedItem, string];
type Stats = Record<string, Rec[]>;

interface PoRecord {
  name: string;
  jiraTitles: string[];
  time: number;
  activeProjects: number;
  notInvoiced: number;
  scores: number;
  salesLogged: number;
  teamLogged: number;
  poOwnLogged: number;
  responsibilityScore: number;
  teamScore: number;
  poScore: number;
  activeScore: number;
  salesScore: number;
  totalScore: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lcm-po-capacity-table',
  imports: [
    MatToolbarModule,
    MatTableModule,
    MatDatepickerModule,
    MatFormFieldModule,
    FormsModule,
    MatTooltipModule,
    MatButtonModule,
    DecimalPipe,
    MatIconModule,
    MatChipsModule,
  ],
  templateUrl: './po-capacity-table.html',
  styles: [
    'td.bg-separator, th.bg-separator {background: #444;}',
    'td.bg-score, th.bg-score{background: rgb(115 146 194);}',
    `
      :host {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
    `,
  ],
})
export class PoCapacityTable {
  private jira = inject(Jira);
  private userService = inject(UserService);

  items = input.required<readonly AugmentedItem[]>();
  timeTable = input.required<Readonly<TimeTableData>>();

  pending = signal(false);

  private usersResource = resource({
    params: () => ({ users: this.userService.users() }),
    defaultValue: {},
    loader: async ({ params }) => {
      const users = params.users;
      return Object.fromEntries(
        users.map((u) => [u.FullName, { id: u.JiraId, capacity: u.Capacity, salesCapacity: u.SalesCapacity }]),
      ) as UserMap;
    },
  });

  private userMap = computed(() => this.usersResource.value());

  private poLoggedResource = resource({
    params: () => ({
      userMap: this.userMap(),
      items: this.items(),
      start: this.poStart(),
      end: this.poEnd(),
    }),
    defaultValue: {},
    loader: async ({ params }) => {
      if (!params.start || !params.end) {
        return {};
      }
      const stats = params.items.reduce(
        (result, item) => {
          const logs = result[item.ProductOwner] || [];
          logs.push([item.JiraTitle, 0, item, item.ProductOwner]);
          result[item.ProductOwner] = logs;
          return result;
        },
        {} as Record<string, Rec[]>,
      );

      // Fetch time data from Jira for each PO's projects
      const requests: Promise<[string, ProjectsTime]>[] = [];
      for (const [name, info] of Object.entries(stats)) {
        const projects = info.map(([p]) => p);
        projects.push('SALES'); // Include SALES project for sales logging
        requests.push(
          firstValueFrom(
            this.jira
              .getPoTime(
                params.start.format('YYYY-MM-DD'),
                params.end.format('YYYY-MM-DD'),
                projects,
                params.userMap[name]?.id,
              )
              .pipe(map((result) => [name, result])),
          ),
        );
      }
      this.pending.set(true);
      const results = await Promise.allSettled(requests);

      results.forEach((settled) => {
        if (settled.status === 'rejected') {
          console.error('Failed poLogged request: %s', settled.reason);
        } else {
          const [name, { report, success, poReport }] = settled.value;

          // Update PO's own logged time per project
          this.poOwnLogged.update((data) => ({
            ...data,
            [name]: Object.fromEntries(Object.entries(poReport).map(([k, v]) => [k, v / 3600])),
          }));

          if (!success) {
            console.warn(`Cannot pull PO report for ${name}`);
            return;
          }
          // Update stats with actual time logged (convert from seconds to hours)
          stats[name] = stats[name].map(([p, time, info, owner]) => [p, report[p] / 3600 || time, info, owner]);
        }
      });
      this.pending.set(false);
      return stats as Stats;
    },
  });

  private statsResource = computed(() => {
    const items = this.items();
    const poLogged = this.poLogged();
    const poOwnLogged = untracked(() => this.poOwnLogged());

    // Calculate total time logged by each PO
    const logged = {};
    for (const name in poLogged) {
      const time = poLogged[name].reduce((acc, [, time]) => acc + (+time || 0), 0);
      logged[name] = time;
    }

    const data = items
      .filter((item) => item.ProductOwner && item.Status !== 'On Hold')
      .reduce((result: Record<string, PoRecord>, product) => {
        const val = result[product.ProductOwner] || {
          name: product.ProductOwner,
          jiraTitles: [],
          time: 0,
          activeProjects: 0,
          notInvoiced: 0,
          scores: 0,
          salesLogged: 0,
          teamLogged: 0,
          poOwnLogged: 0,
          responsibilityScore: 0,
          teamScore: 0,
          poScore: 0,
          activeScore: 0,
          salesScore: 0,
          totalScore: 0,
        };
        result[product.ProductOwner] = val;

        val.jiraTitles.push(product.JiraTitle);

        val.notInvoiced += product.NotInvoicedYet;
        val.teamLogged = logged[val.name];

        const own = poOwnLogged[val.name] || {};
        val.salesLogged += own['SALES'] || 0;

        // Sum PO's own logged time excluding SALES
        val.poOwnLogged = Object.entries(own).reduce((s, [k, n]) => (k === 'SALES' ? s : s + n), 0);

        // LCM-226: account only projects that have more than 30min logged by PO
        if (product.Status === 'Active' && own[product.JiraTitle] >= 0.5) {
          ++val.activeProjects;
        }

        return result;
      }, {});

    return Object.values(data);
  });

  source = computed(() => {
    const result = this.statsResource();
    const timeTable = this.timeTable();
    const userMap = this.userMap();
    const poLogged = this.poLogged();

    // Calculate total remaining time across all projects for each PO
    result.forEach((obj) => {
      obj.time = obj.jiraTitles.reduce(
        (total, title) => Math.max((timeTable[title] || { time: 0 }).time, 0) + total,
        0,
      );
    });

    // Find max values for normalization
    let maxActiveProjects = 0;
    let maxLiability = 0;
    let maxTeamLogged = 0;
    for (const item of result) {
      maxActiveProjects = Math.max(maxActiveProjects, item.activeProjects);
      maxLiability = Math.max(maxLiability, item.time);
      maxTeamLogged = Math.max(maxTeamLogged, item.teamLogged);
    }

    // Calculate normalized scores (0-100% of max) multiplied by impact weight
    for (const item of result) {
      item.activeScore = (item.activeProjects / maxActiveProjects) * ACTIVE_IMPACT;
      item.responsibilityScore = (item.time / maxLiability) * RESPONSIBILITY_IMPACT;

      const capacity = userMap[item.name]?.capacity || 1;
      const salesCapacity = userMap[item.name]?.salesCapacity || UserCapacities.Sales;

      item.teamScore = (item.teamLogged / maxTeamLogged) * TEAM_IMPACT;
      item.poScore = (Math.min(item.poOwnLogged, capacity) / capacity) * PO_IMPACT;
      item.salesScore = (Math.min(item.salesLogged, salesCapacity) / salesCapacity) * SALES_IMPACT;
      item.totalScore = item.activeScore + item.responsibilityScore + item.teamScore + item.poScore + item.salesScore;
    }

    // Flatten result: PO summary row followed by individual project rows
    let final: (PoRecord | Rec)[] = [];
    for (const item of result) {
      final.push(item);
      final = final.concat(poLogged[item.name] || []);
    }

    return final;
  });

  trackBy(idx: number, row: PoRecord | Rec) {
    return Array.isArray(row) ? row[0] : row.name;
  }

  private poLogged = computed<Readonly<Stats>>(() => this.poLoggedResource.value());
  poOwnLogged = signal<Readonly<Record<string, Record<string, number>>>>({});

  poStart = signal(this.jira.lastCheckpoint().subtract(6, 'days'));
  poEnd = signal(this.jira.lastCheckpoint().clone());
  expandedPo = signal<Record<string, boolean>>({});

  expandPo(po: string) {
    this.expandedPo.update((values) => ({ ...values, [po]: !values[po] }));
  }

  isProjectRow(_idx: number, row: PoRecord | Rec): boolean {
    return row instanceof Array;
  }
}
